import React, { useEffect, useState } from "react";
import PageHeader from "../../../components/common/PageHeader";
import Select from "react-select";
import {
    assignEmployeeDepartment,
    assignEmployeeDesignation,
    assignEmployeeVerticalLocation,
    assignRolesToUser,
    createCompanyUser,
    createUserProfile,
    getDepartments,
    getDesignations,
    getHRRoles,
    getLocationsByVertical,
    getReportingManagers,
    getVerticals,
} from "../api/hrService";
import { useNavigate } from "react-router-dom";
import "../styles/CreateHRUser.css";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

/* =========================
   TYPES
========================= */
interface HRUserForm {
    // Step 1 – Personal
    // vertical: number[];
    vertical: number | null;

    location: number[];
    firstName: string;
    middleName: string;
    lastName: string;
    contactNo: string;
    dob: string;
    gender: string;
    aadhaar: string;
    pan: string;
    currentAddress: string;
    permanentAddress: string;
    personalEmail: string;
    officeEmail: string;
    joiningDate: string;
    leavingDate: string | null;
    status: string;

    // Step 2 – Professional
    department: number | null;
    roles: number[];
    roleNames: string[];      // names (for API)
    designation: number | null;
    reportingManager: number | null;
    salesTarget: string;

    // Step 3 – Login
    username: string;
    password: string;
    confirmPassword: string;
}

const CreateHRUser: React.FC = () => {
    const navigate = useNavigate();

    const [activeStep, setActiveStep] = useState(0);
    const [createdUserId, setCreatedUserId] = useState<number | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    /* =========================
       OPTIONS & LOADERS
    ========================= */
    const [verticalOptions, setVerticalOptions] = useState<any[]>([]);
    const [locationOptions, setLocationOptions] = useState<any[]>([]);
    const [departmentOptions, setDepartmentOptions] = useState<any[]>([]);
    const [roleOptions, setRoleOptions] = useState<any[]>([]);
    const [designationOptions, setDesignationOptions] = useState<any[]>([]);
    const [managerOptions, setManagerOptions] = useState<any[]>([]);

    const [loadingLocations, setLoadingLocations] = useState(false);
    const [loadingDepartments, setLoadingDepartments] = useState(false);
    const [loadingRoles, setLoadingRoles] = useState(false);
    const [loadingDesignations, setLoadingDesignations] = useState(false);
    const [loadingManagers, setLoadingManagers] = useState(false);

    /* =========================
       FORM STATE
    ========================= */
    const [formData, setFormData] = useState<HRUserForm>({
        // vertical: [],
        vertical: null,
        location: [],
        firstName: "",
        middleName: "",
        lastName: "",
        contactNo: "",
        dob: "",
        gender: "",
        aadhaar: "",
        pan: "",
        currentAddress: "",
        permanentAddress: "",
        personalEmail: "",
        officeEmail: "",
        joiningDate: "",
        leavingDate: "",
        status: "",

        department: null,
        roles: [],
        roleNames: [],
        designation: null,
        reportingManager: null,
        salesTarget: "",

        username: "",
        password: "",
        confirmPassword: "",
    });

    /* =========================
       HELPERS
    ========================= */
    const isUserLocked = Boolean(createdUserId);

    const isEmpty = (v: any) => v === null || v === undefined || v === "";
    const isNonVerticalRole = (roleName?: string) =>
        ["admin", "hr"].includes((roleName || "").toLowerCase());
    const isAdminRole = (roleName?: string) =>
        (roleName || "").toLowerCase() === "admin";

    const selectedRoleName = formData.roleNames?.[0];
    const isTopRole = selectedRoleName?.toLowerCase() === "admin";



    const isValidEmail = (email: string) =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const isValidPhone = (phone: string) => /^[6-9]\d{9}$/.test(phone);

    const isValidAadhaar = (a: string) => /^\d{12}$/.test(a);

    const isValidPAN = (pan: string) =>
        /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: "" }));
    };

    /* =========================
       VALIDATIONS
    ========================= */
    const validateStep1 = () => {
        const e: Record<string, string> = {};

        const roleName = formData.roleNames?.[0] || "";
        const skipVertical = isNonVerticalRole(roleName);

        // 🔥 IMPORTANT FIX
//         if (!formData.roleNames?.length) {
//     e.roles = "Role is required";
// }
        if (!skipVertical) {
            if (!formData.vertical) e.vertical = "Vertical is required";
            if (!formData.location.length) e.location = "Location is required";
        }
        if (isEmpty(formData.firstName)) e.firstName = "First name is required";
        if (isEmpty(formData.lastName)) e.lastName = "Last name is required";

        if (isEmpty(formData.contactNo))
            e.contactNo = "Contact number is required";
        else if (!isValidPhone(formData.contactNo))
            e.contactNo = "Invalid mobile number";

        if (!isEmpty(formData.personalEmail) && !isValidEmail(formData.personalEmail)) {
            e.personalEmail = "Invalid email";
        }

        // Office Email – OPTIONAL
        if (!isEmpty(formData.officeEmail) && !isValidEmail(formData.officeEmail)) {
            e.officeEmail = "Invalid email";
        }

        if (isEmpty(formData.gender)) e.gender = "Gender is required";
        if (isEmpty(formData.status)) {
            e.status = "Status is required";
        }

        if (isEmpty(formData.dob))
            e.dob = "Date of birth is required";
        else if (new Date(formData.dob) >= new Date())
            e.dob = "DOB must be in past";

        if (isEmpty(formData.aadhaar))
            e.aadhaar = "Aadhaar is required";
        else if (!isValidAadhaar(formData.aadhaar))
            e.aadhaar = "Aadhaar must be 12 digits";

        if (!isEmpty(formData.pan) && !isValidPAN(formData.pan))
            e.pan = "Invalid PAN";

        if (isEmpty(formData.currentAddress))
            e.currentAddress = "Current address required";

        if (isEmpty(formData.permanentAddress))
            e.permanentAddress = "Permanent address required";

        if (isEmpty(formData.joiningDate))
            e.joiningDate = "Joining date required";

        if (
            formData.leavingDate &&
            new Date(formData.leavingDate) < new Date(formData.joiningDate)
        )
            e.leavingDate = "Leaving date must be after joining date";

        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const validateStep2 = () => {
        const e: Record<string, string> = {};
        if (!formData.department) e.department = "Department required";
        if (!formData.roles.length) e.roles = "At least one role required";
        if (!formData.designation) e.designation = "Designation required";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const validateStep3 = () => {
        const e: Record<string, string> = {};

        if (isEmpty(formData.username))
            e.username = "Username required";
        else if (formData.username.length < 4)
            e.username = "Minimum 4 characters";

        if (isEmpty(formData.password))
            e.password = "Password required";
        else if (formData.password.length < 8)
            e.password = "Min 8 characters";
        else if (!/(?=.*[A-Z])(?=.*\d)/.test(formData.password))
            e.password = "1 uppercase & 1 number required";

        if (formData.password !== formData.confirmPassword)
            e.confirmPassword = "Passwords do not match";

        setErrors(e);
        return Object.keys(e).length === 0;
    };

    /* =========================
       FETCH HELPERS
    ========================= */
    const fetchLocationsFromVertical = async (verticalId: number | null) => {
        if (!verticalId) {
            setLocationOptions([]);
            return;
        }

        setLoadingLocations(true);
        try {
            const res = await getLocationsByVertical(verticalId);
            setLocationOptions(
                res.data.results.map((l: any) => ({
                    value: l.id,
                    label: l.name,
                }))
            );
        } finally {
            setLoadingLocations(false);
        }
    };



    useEffect(() => {
        const role = formData.roleNames?.[0]?.toLowerCase();

        if (!role) return;

        /* ADMIN or HR */
        if (role === "admin" || role === "hr") {
            setFormData(prev => ({
                ...prev,
                vertical: null,
                location: [],
                reportingManager: null
            }));
        }
    }, [formData.roleNames]);

    useEffect(() => {
        if (formData.vertical) {
            fetchLocationsFromVertical(formData.vertical);
        } else {
            setLocationOptions([]);
        }
    }, [formData.vertical]);







    const handleReportingManagerLoad = async () => {
        const role = formData.roleNames?.[0]?.toLowerCase();

        /* ================= CASE 1 : ADMIN ================= */
        if (role === "admin") {
            setManagerOptions([]);        // hide field automatically
            return;                       // ❌ no API call
        }

        /* ================= CASE 2 : HR ================= */
        if (role === "hr") {
            setLoadingManagers(true);
            try {
                const res = await getReportingManagers({
                    role: "Hr"        // ONLY role (no vertical/location)
                });

                const managers = res?.data?.results || [];
                setManagerOptions(
                    managers.map((m: any) => ({
                        value: m.id,
                        label: m.full_name || m.email
                    }))
                );
            } finally {
                setLoadingManagers(false);
            }
            return;
        }


        /* ================= CASE 3 : OTHER ROLES ================= */
        if (!formData.vertical || !formData.location.length) {
            setManagerOptions([]);
            return;
        }


        setLoadingManagers(true);
        try {
            const res = await getReportingManagers({
                vertical_ids: [formData.vertical],
                role: formData.roleNames[0],
                location_ids: formData.location,
            });

            const managers = res?.data?.results || [];
            setManagerOptions(
                managers.map((m: any) => ({
                    value: m.id,
                    label: m.full_name || m.email
                }))
            );
        } finally {
            setLoadingManagers(false);
        }
    };



    /* =========================
       STEP NAVIGATION
    ========================= */
    const handleNext = () => {
        if (activeStep === 0 && !validateStep1()) return;
        if (activeStep === 1 && !validateStep2()) return;
        setActiveStep(s => s + 1);
    };

    /* =========================
       FINAL SUBMIT
    ========================= */
    const handleSubmit = async () => {
        const validStep1 = validateStep1();
        const validStep2 = validateStep2();
        const validStep3 = validateStep3();

        // jump to first invalid step
        if (!validStep1) {
            setActiveStep(0);
            return;
        }
        if (!validStep2) {
            setActiveStep(1);
            return;
        }
        if (!validStep3) {
            setActiveStep(2);
            return;
        }


        setSubmitting(true);
        try {
            const userRes = await createCompanyUser({
                username: formData.username,
                email: formData.officeEmail,
                password: formData.password,
                confirm_password: formData.confirmPassword,
                user_role: [],
                user_permissions: [],
            });

            const userId = userRes.data.data.id;

            const profileRes = await createUserProfile({
                user: userId,
                first_name: formData.firstName,
                middle_name: formData.middleName,
                last_name: formData.lastName,
                aadhar_no: formData.aadhaar,
                pan_no: formData.pan,
                gender_type: formData.gender,
                dob: formData.dob,
                joining_date: formData.joiningDate,
                leaving_date: formData.leavingDate || null,
                mobile: formData.contactNo,
                personal_email_id: formData.personalEmail,
                official_mail_id: formData.officeEmail,
                current_address: formData.currentAddress,
                permanent_address: formData.permanentAddress,
                is_active: formData.status,
            });

            const employeeId = profileRes.data.data.id;
            console.log("employee id", employeeId);

            const selectedRole = formData.roleNames?.[0];
            const skipVertical = isNonVerticalRole(selectedRole);


            if (!skipVertical && !formData.vertical) {
                toast.error("Vertical is required");
                setSubmitting(false);
                return;
            }


            const promises: Promise<any>[] = [];

            if (!skipVertical && formData.vertical) {
                promises.push(
                    assignEmployeeVerticalLocation({
                        employee_id: employeeId,
                        vertical_locations: [
                            {
                                vertical_id: formData.vertical,
                                location_ids: formData.location,
                            },
                        ],
                    })
                );
            }

            promises.push(
                assignEmployeeDepartment({
                    employee_id: employeeId,
                    department_id: formData.department,
                })
            );

            const selectedRoleName = formData.roleNames?.[0];
            const isAdmin = isAdminRole(selectedRoleName);

            const designationPayload: any = {
                employee_id: employeeId,
                designation_id: formData.designation,
                sales_target: formData.salesTarget,
            };

            if (!isAdmin) {
                designationPayload.assign_reporting = formData.reportingManager;
            }

            promises.push(assignEmployeeDesignation(designationPayload));

            await Promise.all(promises);   // ⭐ MUST ADD

            await assignRolesToUser({
                user_id: userId,
                role: formData.roleNames?.[0] || "",
            });





            toast.success("Employee created successfully 🎉");
            navigate("/hr/users", { replace: true });
        } catch (err: any) {
            if (err?.response?.data) {
                const apiErrors = err.response.data;
                const fieldErrors: Record<string, string> = {};

                // Map backend errors → form fields
                if (apiErrors.username?.length) {
                    fieldErrors.username = apiErrors.username[0];
                }

                if (apiErrors.email?.length) {
                    fieldErrors.officeEmail = apiErrors.email[0]; // map email → officeEmail
                }

                setErrors(fieldErrors);

                // Optional toast (UX choice)
                toast.error("Please fix highlighted errors");
                return;
            }

            toast.error("Employee creation failed");
        } finally {
            setSubmitting(false);
        }
    };
    const selectStyles = {
        control: (base: any, state: any) => ({
            ...base,
            backgroundColor: "#fff",          // ✅ white background
            borderColor: state.isFocused ? "#86b7fe" : "#ced4da",
            boxShadow: "none",
            minHeight: "58px",                 // matches Bootstrap input
            "&:hover": {
                borderColor: "#86b7fe",
            },
        }),

        menu: (base: any) => ({
            ...base,
            backgroundColor: "#fff",
            zIndex: 9999,
        }),

        option: (base: any, state: any) => ({
            ...base,
            backgroundColor: state.isFocused
                ? "#f8f9fa"
                : "#fff",
            color: "#212529",
        }),

        multiValue: (base: any) => ({
            ...base,
            backgroundColor: "#e9ecef",
        }),

        multiValueLabel: (base: any) => ({
            ...base,
            color: "#212529",
        }),

        multiValueRemove: (base: any) => ({
            ...base,
            ":hover": {
                backgroundColor: "#dc3545",
                color: "#fff",
            },
        }),
    };


    /* =========================
       MASTER DATA
    ========================= */
    useEffect(() => {
        getVerticals().then(res =>
            setVerticalOptions(
                res.data.results.map((v: any) => ({
                    value: v.id,
                    label: v.name,
                }))
            )
        );
    }, []);

    useEffect(() => {
        if (activeStep !== 1) return;

        const role = formData.roleNames?.[0];
        if (!role) return;

        handleReportingManagerLoad();
    }, [activeStep, formData.roleNames, formData.vertical, formData.location]);

    const statusOptions = [
        { value: "Active", label: "Active" },
        { value: "Inactive", label: "Inactive" },
    ];

    useEffect(() => {
        if (activeStep === 1) {
            getDepartments().then(res =>
                setDepartmentOptions(
                    res.data.results.map((d: any) => ({
                        value: d.id,
                        label: d.name,
                    }))
                )
            );
            getHRRoles().then(res =>
                setRoleOptions(
                    res.data.data.map((r: any) => ({
                        value: r.id,
                        label: r.name,
                        name: r.name,        // ⭐ IMPORTANT
                    }))
                )
            );


            getDesignations().then(res =>
                setDesignationOptions(
                    res.data.results.map((d: any) => ({
                        value: d.id,
                        label: d.name,
                    }))
                )
            );
        }
    }, [activeStep]);
    const genderOptions = [
        { value: "Male", label: "Male" },
        { value: "Female", label: "Female" },
        { value: "Other", label: "Other" },
    ];
    const today = new Date().toISOString().split("T")[0];
    const todayDate = new Date();

    const yesterday = new Date(todayDate);
    yesterday.setDate(todayDate.getDate() - 1);

    const maxDob = yesterday.toISOString().split("T")[0];

    const preventTypingDate = (e: React.KeyboardEvent<HTMLInputElement>) => {
        e.preventDefault();
    };
    return (
        <>
            <PageHeader
                title="Create Employee"
                breadcrumbs={[
                    { label: "Dashboard", path: "/dashboard" },
                    { label: "HR" },
                    { label: "Employee Master", path: "/hr/users" },
                    { label: "Create Employee" },
                ]}
            />

            <div className="bg-gray">
                <div className="container pt-4 pb-4">

                    {/* ================= STEP HEADER ================= */}
                    <div className="d-flex mb-4">
                        {["Personal Details", "Professional Details", "Login Details"].map(
                            (step, index) => (
                                <div
                                    key={step}
                                    className={`flex-fill text-center p-2 border ${index === activeStep
                                        ? "bg-primary text-white fw-bold"
                                        : "bg-white"
                                        }`}
                                >
                                    {step}
                                </div>
                            )
                        )}
                    </div>

                    <div className="card p-4">

                        {/* ================= STEP 0 – PERSONAL DETAILS ================= */}
                        {activeStep === 0 && (
                            <div className="row g-4">

                                {/* Vertical */}
                                <div className="col-md-3">
                                    <label className="form-label fw-semibold">Vertical *</label>
                                    <Select
                                        options={verticalOptions}
                                        value={
                                            verticalOptions.find(o => o.value === formData.vertical) || null
                                        }
                                        onChange={(selected) => {
                                            const value = selected?.value || null;

                                            setFormData(prev => ({
                                                ...prev,
                                                vertical: value,
                                                location: [],
                                                reportingManager: null,
                                            }));

                                            fetchLocationsFromVertical(value);

                                            setErrors(prev => ({ ...prev, vertical: "" }));
                                        }}
                                        styles={selectStyles}
                                    />

                                    {errors.vertical && (
                                        <small className="text-danger">{errors.vertical}</small>
                                    )}
                                </div>

                                {/* Location */}
                                <div className="col-md-3">
                                    <label className="form-label fw-semibold">Location *</label>
                                    <Select
                                        isMulti
                                        options={locationOptions}
                                        value={locationOptions.filter(o =>
                                            formData.location.includes(o.value)
                                        )}
                                        styles={selectStyles}   // ✅ THIS LINE

                                        onChange={(selected) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                location: selected.map(s => s.value),
                                            }));
                                            setErrors(prev => ({ ...prev, location: "" }));
                                        }}
                                        isDisabled={!formData.vertical || loadingLocations}

                                    />
                                    {errors.location && (
                                        <small className="text-danger">{errors.location}</small>
                                    )}
                                </div>


                                <div className="col-md-6" />

                                {/* Name */}
                                <div className="col-md-3">
                                    <div className="form-floating">
                                        <input
                                            className={`form-control ${errors.firstName && "is-invalid"}`}
                                            name="firstName"
                                            placeholder="First Name *"
                                            value={formData.firstName}
                                            onChange={handleChange}
                                        />
                                        <label>First Name*</label>
                                        {errors.firstName && (
                                            <div className="invalid-feedback">{errors.firstName}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="col-md-3">
                                    <div className="form-floating">
                                        <input
                                            className="form-control"
                                            name="middleName"
                                            placeholder="Middle Name"
                                            value={formData.middleName}
                                            onChange={handleChange}
                                        />
                                        <label>Middle Name</label>
                                    </div>
                                </div>

                                <div className="col-md-3">
                                    <div className="form-floating">
                                        <input
                                            className={`form-control ${errors.lastName && "is-invalid"}`}
                                            name="lastName"
                                            placeholder="Last Name *"
                                            value={formData.lastName}
                                            onChange={handleChange}
                                        />
                                        <label>Last Name *</label>
                                        {errors.lastName && (
                                            <div className="invalid-feedback">{errors.lastName}</div>
                                        )}
                                    </div>
                                </div>

                                {/* Contact */}
                                <div className="col-md-3">
                                    <div className="form-floating">
                                        <input
                                            className={`form-control ${errors.contactNo && "is-invalid"}`}
                                            name="contactNo"
                                            placeholder="Contact No *"
                                            value={formData.contactNo}
                                            onChange={handleChange}
                                        />
                                        <label>Contact No *</label>
                                        {errors.contactNo && (
                                            <div className="invalid-feedback">{errors.contactNo}</div>
                                        )}
                                    </div>
                                </div>

                                {/* Emails */}
                                <div className="col-md-3">
                                    <div className="form-floating">
                                        <input
                                            className={`form-control ${errors.personalEmail && "is-invalid"}`}
                                            name="personalEmail"
                                            placeholder="Personal Email"
                                            value={formData.personalEmail}
                                            onChange={handleChange}
                                        />
                                        <label>Personal Email</label>
                                        {errors.personalEmail && (
                                            <div className="invalid-feedback">{errors.personalEmail}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="col-md-3">
                                    <div className="form-floating">
                                        <input
                                            className={`form-control ${errors.officeEmail && "is-invalid"}`}
                                            name="officeEmail"
                                            placeholder="Office Email"
                                            value={formData.officeEmail}
                                            onChange={handleChange}
                                        />
                                        <label>Office Email</label>
                                        {errors.officeEmail && (
                                            <div className="invalid-feedback">{errors.officeEmail}</div>
                                        )}
                                    </div>
                                </div>

                                {/* DOB & Gender */}
                                <div className="col-md-3">
                                    <div className="form-floating">
                                        <input
                                            type="date"
                                            className={`form-control ${errors.dob && "is-invalid"}`}
                                            name="dob"
                                            value={formData.dob}
                                            onChange={handleChange}

                                            max={maxDob}
                                            onKeyDown={(e) => e.preventDefault()} // block typing
                                            onPaste={(e) => e.preventDefault()}   // block paste
                                            onClick={(e) => (e.currentTarget as HTMLInputElement).showPicker()} // force picker
                                        />
                                        <label>Date of Birth *</label>
                                        {errors.dob && (
                                            <div className="invalid-feedback">{errors.dob}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="col-md-3 stage-list">
                                    <div className="form-floating">
                                        <Select
                                            options={genderOptions}
                                            value={
                                                genderOptions.find(g => g.value === formData.gender) || null
                                            }
                                            onChange={(selected) => {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    gender: selected?.value || "",
                                                }));
                                                setErrors(prev => ({ ...prev, gender: "" }));
                                            }}
                                            placeholder="Select Gender *"
                                            isSearchable={false}   // 🚫 NO SEARCH
                                            classNamePrefix="react-select"
                                            className={errors.gender ? "is-invalid-select" : ""}
                                        />

                                        {errors.gender && (
                                            <small className="text-danger">{errors.gender}</small>
                                        )}
                                    </div>
                                </div>


                                {/* Aadhaar / PAN */}
                                <div className="col-md-3">
                                    <div className="form-floating">
                                        <input
                                            className="form-control"
                                            name="aadhaar"
                                            placeholder="Aadhaar *"
                                            value={formData.aadhaar}
                                            onChange={handleChange}
                                        />
                                        <label>Aadhaar *</label>
                                        {errors.aadhaar && (
                                            <small className="text-danger">{errors.aadhaar}</small>
                                        )}
                                    </div>
                                </div>

                                <div className="col-md-3">
                                    <div className="form-floating">
                                        <input
                                            className="form-control"
                                            name="pan"
                                            placeholder="PAN"
                                            value={formData.pan}
                                            onChange={handleChange}
                                        />
                                        <label>PAN</label>
                                        {errors.pan && (
                                            <small className="text-danger">{errors.pan}</small>
                                        )}
                                    </div>
                                </div>

                                {/* Address */}
                                <div className="col-md-3">
                                    <div className="form-floating">
                                        <input
                                            className={`form-control ${errors.currentAddress && "is-invalid"}`}
                                            name="currentAddress"
                                            placeholder="Current Address *"
                                            value={formData.currentAddress}
                                            onChange={handleChange}
                                        />
                                        <label>Current Address *</label>
                                        {errors.currentAddress && (
                                            <small className="text-danger">{errors.currentAddress}</small>
                                        )}
                                    </div>
                                </div>

                                <div className="col-md-3">
                                    <div className="form-floating">
                                        <input
                                            className={`form-control ${errors.permanentAddress && "is-invalid"}`}
                                            name="permanentAddress"
                                            placeholder="Permanent Address *"
                                            value={formData.permanentAddress}
                                            onChange={handleChange}
                                        />
                                        <label>Permanent Address *</label>
                                        {errors.permanentAddress && (
                                            <small className="text-danger">{errors.permanentAddress}</small>
                                        )}
                                    </div>
                                </div>

                                {/* Joining / Leaving */}
                                <div className="col-md-3">
                                    <div className="form-floating">
                                        <input
                                            type="date"
                                            className={`form-control ${errors.joiningDate && "is-invalid"}`}
                                            name="joiningDate"
                                            value={formData.joiningDate}
                                            onChange={handleChange}
                                        />
                                        <label>Joining Date *</label>

                                        {errors.joiningDate && (
                                            <small className="text-danger">{errors.joiningDate}</small>
                                        )}
                                    </div>
                                </div>
                                <div className="col-md-3 stage-list" >
                                    <div className="form-floating">
                                        <Select
                                            options={statusOptions}
                                            isSearchable={false}
                                            value={
                                                statusOptions.find(opt => opt.value === formData.status) || null
                                            }
                                            onChange={(selected) => {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    status: selected?.value || "",
                                                    ...(selected?.value === "Active"
                                                        ? { leavingDate: null }
                                                        : {}),
                                                }));
                                                setErrors(prev => ({ ...prev, status: "" }));
                                            }}

                                            placeholder="Status *"
                                            classNamePrefix="react-select"
                                            className={errors.status ? "is-invalid-select" : ""}
                                        />

                                        {errors.status && (
                                            <small className="text-danger">{errors.status}</small>
                                        )}
                                    </div>
                                </div>



                                {formData.status === "Inactive" && (
                                    <div className="col-md-3">
                                        <div className="form-floating">
                                            <input
                                                type="date"
                                                className={`form-control ${errors.leavingDate ? "is-invalid" : ""}`}
                                                name="leavingDate"
                                                value={formData.leavingDate || ""}
                                                onChange={handleChange}
                                            />
                                            <label>Leaving Date</label>
                                            {errors.leavingDate && (
                                                <small className="text-danger">{errors.leavingDate}</small>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ================= STEP 1 – PROFESSIONAL DETAILS ================= */}
                        {activeStep === 1 && (
                            <div className="row g-4">
                                <div className="col-md-6 stage-list">
                                    <div className="form-floating">
                                        <Select
                                            options={departmentOptions}
                                            value={
                                                departmentOptions.find(o => o.value === formData.department) || null
                                            }
                                            onChange={(selected) => {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    department: selected?.value || null,
                                                }));
                                                setErrors(prev => ({ ...prev, department: "" }));
                                            }}
                                            placeholder="Department *"
                                            classNamePrefix="react-select"
                                        />

                                        {errors.department && (
                                            <small className="text-danger">{errors.department}</small>
                                        )}
                                    </div>
                                </div>


                                <div className="col-md-6 stage-list">
                                    <div className="form-floating">
                                        <Select
                                            isMulti
                                            options={roleOptions}
                                            value={roleOptions.filter(o =>
                                                formData.roles.includes(o.value)
                                            )}
                                            onChange={(selected) => {
                                                const last = selected.slice(-1);
                                                const roleName = last[0]?.name;
                                                const nonVertical = isNonVerticalRole(roleName);

                                                const nextVertical = nonVertical ? null : formData.vertical;
                                                const nextLocation = nonVertical ? [] : formData.location;

                                                setFormData(prev => ({
                                                    ...prev,
                                                    roles: last.map(s => s.value),
                                                    roleNames: last.map(s => s.name),
                                                    ...(nonVertical && {
                                                        vertical: null,
                                                        location: [],
                                                        reportingManager: null
                                                    }),
                                                }));

                                                /* ===== CASE 3 ===== */
                                                if (!nonVertical && (!nextVertical || !nextLocation.length)) {
                                                    toast.info("Please select Vertical and Location first");
                                                    setTimeout(() => setActiveStep(0), 0);
                                                }

                                                /* ===== CASE 1 ===== */
                                                if (nonVertical) {
                                                    Swal.fire({
                                                        icon: "info",
                                                        title: "Vertical not required",
                                                        text: `${roleName} users are not vertical specific. Vertical and Location cleared.`,
                                                        confirmButtonColor: "#3085d6",
                                                    });
                                                }

                                                setErrors(prev => ({ ...prev, roles: "" }));
                                            }}


                                            placeholder="Role *"
                                            classNamePrefix="react-select"
                                        />


                                        {errors.roles && (
                                            <small className="text-danger">{errors.roles}</small>
                                        )}


                                        {errors.role && (
                                            <small className="text-danger">{errors.role}</small>
                                        )}
                                    </div>
                                </div>


                                <div className="col-md-6 stage-list">
                                    <div className="form-floating">
                                        <Select
                                            options={designationOptions}
                                            value={
                                                designationOptions.find(o => o.value === formData.designation) || null
                                            }
                                            onChange={(selected) => {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    designation: selected?.value || null,
                                                }));
                                                setErrors(prev => ({ ...prev, designation: "" }));
                                            }}
                                            placeholder="Designation *"
                                            classNamePrefix="react-select"
                                        />

                                        {errors.designation && (
                                            <small className="text-danger">{errors.designation}</small>
                                        )}
                                    </div>
                                </div>


                                {!isTopRole && (
                                    <div className="col-md-6 stage-list">
                                        <div className="form-floating">
                                            <Select
                                                isClearable
                                                options={managerOptions}
                                                value={
                                                    managerOptions.find(o => o.value === formData.reportingManager) || null
                                                }
                                                onChange={(selected) => {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        reportingManager: selected?.value || null,
                                                    }));
                                                    setErrors(prev => ({ ...prev, reportingManager: "" }));
                                                }}
                                                placeholder="Reporting Manager"
                                                classNamePrefix="react-select"
                                            />

                                            {errors.reportingManager && (
                                                <small className="text-danger">{errors.reportingManager}</small>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className="col-md-6 ">
                                    <div className="form-floating">
                                        <input
                                            type="number"
                                            className={`form-control ${errors.salesTarget ? "is-invalid" : ""}`}
                                            name="salesTarget"
                                            placeholder="Sales Target"
                                            value={formData.salesTarget}
                                            onChange={handleChange}
                                        />
                                        <label>Sales Target (₹)</label>

                                        {errors.salesTarget && (
                                            <div className="invalid-feedback">{errors.salesTarget}</div>
                                        )}
                                    </div>
                                </div>

                            </div>
                        )}

                        {/* ================= STEP 2 – LOGIN DETAILS ================= */}
                        {activeStep === 2 && (
                            <div className="row g-4">

                                {/* Username */}
                                <div className="col-md-4">
                                    <div className="form-floating">
                                        <input
                                            className={`form-control ${errors.username ? "is-invalid" : ""}`}
                                            name="username"
                                            placeholder="Username *"
                                            value={formData.username}
                                            onChange={handleChange}
                                        />
                                        <label>Username *</label>
                                        {errors.username && (
                                            <div className="invalid-feedback">{errors.username}</div>
                                        )}
                                    </div>
                                </div>

                                {/* Password */}
                                <div className="col-md-4">
                                    <div className="form-floating">
                                        <input
                                            type="password"
                                            className={`form-control ${errors.password ? "is-invalid" : ""}`}
                                            name="password"
                                            placeholder="Password *"
                                            value={formData.password}
                                            onChange={handleChange}
                                        />
                                        <label>Password *</label>
                                        {errors.password && (
                                            <div className="invalid-feedback">{errors.password}</div>
                                        )}
                                    </div>
                                </div>

                                {/* Confirm Password */}
                                <div className="col-md-4">
                                    <div className="form-floating">
                                        <input
                                            type="password"
                                            className={`form-control ${errors.confirmPassword ? "is-invalid" : ""}`}
                                            name="confirmPassword"
                                            placeholder="Confirm Password *"
                                            value={formData.confirmPassword}
                                            onChange={handleChange}
                                        />
                                        <label>Confirm Password *</label>
                                        {errors.confirmPassword && (
                                            <div className="invalid-feedback">{errors.confirmPassword}</div>
                                        )}
                                    </div>
                                </div>

                            </div>
                        )}


                        {/* ================= FOOTER ================= */}
                        <div className="form-footer d-flex justify-content-end mt-4 gap-2">
                            {/* LEFT SIDE */}
                            {activeStep === 0 ? (
                                <button
                                    className="btn btn-outline-secondary "
                                    onClick={() => navigate("/hr/users")}
                                >
                                    Cancel
                                </button>
                            ) : (
                                <button
                                    className="btn btn-secondary"
                                    onClick={() => setActiveStep(s => s - 1)}
                                >
                                    Back
                                </button>
                            )}



                            <button
                                className="btn btn-primary"
                                onClick={activeStep === 2 ? handleSubmit : handleNext}
                                disabled={submitting}
                            >
                                {submitting ? "Saving..." : activeStep === 2 ? "Save" : "Save & Next"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );

};

export default CreateHRUser;
